import React, { useState, useEffect } from "react";
import { getRequest } from "../../service/index.js";
import { formatDate } from "../../utils/dateTime";

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const getNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle day click to show modal
  const handleDayClick = (day, dayEvents) => {
    if (dayEvents.length > 0) {
      setSelectedDate({
        day,
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), day),
        events: dayEvents
      });
      setShowModal(true);
    }
  };

  // Test function to verify date calculation
  const testDateCalculation = (date) => {
    // Get first day of the month (1st)
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    // Get last day of the month (last day of current month)
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    
    // Format as YYYY-MM-DD without timezone issues
    const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    
    console.log(`Testing date calculation for ${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}:`);
    console.log(`Start: ${startDateStr}, End: ${endDateStr}`);
    return { startDateStr, endDateStr };
  };

  // Fetch events for the current month
  const fetchEvents = async (date) => {
    setLoading(true);
    try {
      // Test and verify date calculation
      const { startDateStr, endDateStr } = testDateCalculation(date);
      
      console.log(`API Call: /api/companies/employeeevents?startDate=${startDateStr}&endDate=${endDateStr}&type=all`);
      
      const data = await getRequest(
        `/api/companies/employeeevents?startDate=${startDateStr}&endDate=${endDateStr}&type=all`
      );
      
      setEvents(data.events || []);
      console.log('Events fetched:', data.events?.length || 0);
      console.log('Sample events:', data.events?.slice(0, 2));
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch events when month changes
  useEffect(() => {
    fetchEvents(currentDate);
  }, [currentDate]);

  // Get events for a specific date
  const getEventsForDate = (day) => {
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const targetMonth = targetDate.getMonth();
    const targetDay = targetDate.getDate();
    
    return events.filter(event => {
      // Filter out joining events
      if (event.eventType === 'joining') {
        return false;
      }
      
      const eventDate = new Date(event.eventDate);
      const eventMonth = eventDate.getMonth();
      const eventDay = eventDate.getDate();
      
      // For birthdays and anniversaries, match month and day regardless of year
      if (event.eventType === 'birthday' || event.eventType === 'anniversary') {
        return eventMonth === targetMonth && eventDay === targetDay;
      }
      
      // For other events, match exact date
      const eventDateStr = eventDate.toISOString().split('T')[0];
      const targetDateStr = targetDate.toISOString().split('T')[0];
      return eventDateStr === targetDateStr;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                          currentDate.getFullYear() === today.getFullYear();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square bg-transparent border-none cursor-default"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === today.getDate();
      const dayEvents = getEventsForDate(day);
      
      days.push(
        <div 
          key={day} 
          className={`aspect-square flex flex-col bg-white border border-gray-200 cursor-pointer text-sm font-medium transition-all relative p-1 min-h-20 ${
            isToday ? 'bg-primary text-white' : ''
          } ${dayEvents.length > 0 ? 'border-primary' : ''} hover:bg-gray-50 hover:border-primary`}
          onClick={() => handleDayClick(day, dayEvents)}
        >
          <div className={`font-semibold mb-1 text-center ${isToday ? 'text-white' : ''}`}>{day}</div>
          {dayEvents.length > 0 && (
            <div className="flex-1 flex flex-col gap-px overflow-hidden">
              {dayEvents.slice(0, 4).map((event, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between px-1 py-0.5 rounded text-xs font-medium whitespace-nowrap overflow-hidden ${
                    event.eventType === 'birthday' 
                      ? 'bg-red-50 text-red-800 border-l-2 border-l-pink-500' 
                      : 'bg-green-50 text-green-800 border-l-2 border-l-green-500'
                  } ${isToday ? 'bg-white bg-opacity-20 text-white border-l-white border-l-opacity-50' : ''}`}
                  title={`${event.firstName} ${event.lastName} - ${event.eventType}${event.eventType === 'birthday' ? ` (Age: ${event.age})` : event.eventType === 'anniversary' ? ` (${event.yearsOfService} years)` : ''}`}
                >
                  <span className="flex-1 overflow-hidden text-ellipsis">{event.firstName}</span>
                  <span className="ml-1 text-xs">
                    {event.eventType === 'birthday' ? '🎂' : 
                     event.eventType === 'anniversary' ? '🎉' : '📅'}
                  </span>
                </div>
              ))}
              {dayEvents.length > 4 && (
                <div className="bg-gray-100 text-gray-600 text-center py-0.5 px-1 rounded text-xs font-semibold">+{dayEvents.length - 4}</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="max-w-4xl mx-auto my-5 bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="flex justify-between items-center p-6 bg-primary text-white">
        <div>
          <h2 className="text-2xl font-semibold m-0">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
          {loading && <div className="text-xs opacity-80 mt-1">Loading events...</div>}
          <div className="text-xs opacity-70 mt-1 font-mono">
            {new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0]} to {new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split('T')[0]}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={getPreviousMonth} className="px-4 py-2 border-none rounded-md bg-[#5947ad] bg-opacity-20 text-white cursor-pointer text-sm font-medium transition-colors hover:bg-opacity-30">
            ←
          </button>
          <button onClick={goToToday} className="px-4 py-2 border-none rounded-md bg-white bg-opacity-90 text-primary cursor-pointer text-sm font-medium transition-colors hover:bg-white">
            Today
          </button>
          <button onClick={getNextMonth} className="px-4 py-2 border-none rounded-md bg-[#5947ad] bg-opacity-20 text-white cursor-pointer text-sm font-medium transition-colors hover:bg-opacity-30">
            →
          </button>
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-7 gap-px mb-2">
          {dayNames.map(day => (
            <div key={day} className="p-3 text-center font-semibold text-gray-600 bg-gray-50 text-sm">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px">
          {renderCalendar()}
        </div>
      </div>


      {/* Events Modal */}
      {showModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-11/12 max-h-4/5 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-primary text-white">
              <h3 className="text-lg font-semibold m-0">Events for {formatDate(selectedDate.date, 'dddd, MMMM D, YYYY')}</h3>
              <button className="bg-none border-none text-white text-2xl cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors hover:bg-white hover:bg-opacity-20" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {selectedDate.events.map((event, index) => (
                <div key={index} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg mb-3 bg-gray-50">
                  <div className="flex-1">
                    <div className="font-semibold text-base text-gray-800 mb-1">
                      {event.firstName} {event.lastName}
                    </div>
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span className="bg-gray-200 px-2 py-1 rounded">{event.employee_code}</span>
                      <span className="bg-gray-200 px-2 py-1 rounded">{event.department.name}</span>
                      <span className="bg-gray-200 px-2 py-1 rounded">{event.designation.designationName}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`px-3 py-1.5 rounded-full text-xs font-semibold mb-1 ${
                      event.eventType === 'birthday' 
                        ? 'bg-red-50 text-red-800' 
                        : 'bg-green-50 text-green-800'
                    }`}>
                      {event.eventType === 'birthday' ? '🎂 Birthday' : '🎉 Anniversary'}
                    </div>
                    {event.eventType === 'birthday' && (
                      <div className="text-xs text-gray-600">Age: {event.age}</div>
                    )}
                    {event.eventType === 'anniversary' && (
                      <div className="text-xs text-gray-600">{event.yearsOfService} years of service</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Calendar;